import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Paper, Divider, CircularProgress,
  Alert, TextField, Button, Select, MenuItem, InputLabel, FormControl
} from "@mui/material";
import { useParams } from "react-router-dom";

const ViewLead = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setLoading(true);
    fetch(`https://core-bankers-backend.vercel.app/get-lead/${id}`)
      .then(res => res.json())
      .then(data => {
        if (data?.lead) setLead(data.lead);
        else throw new Error(data.message || "Lead not found");
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLead((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    setError("");
    setSuccess("");
    try {
      const res = await fetch("https://core-bankers-backend.vercel.app/update-lead", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(lead)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Update failed");
      setSuccess("Lead updated successfully.");
      setEditing(false);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Box p={4}><CircularProgress /></Box>;
  if (error && !lead) return <Box p={4}><Alert severity="error">{error}</Alert></Box>;
  if (!lead) return null;

  return (
    <Box sx={{ p: 4 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
        <Typography variant="h6" gutterBottom>Lead Details</Typography>
        <Divider sx={{ mb: 3 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={6}><strong>Name:</strong> {lead.name}</Grid>
          <Grid item xs={6}><strong>Mobile:</strong> {lead.mobile}</Grid>

          <Grid item xs={6}>
            {editing ? (
              <TextField
                name="loan_type"
                label="Loan Type"
                value={lead.loan_type || ""}
                onChange={handleChange}
                fullWidth
              />
            ) : (
              <><strong>Loan Type:</strong> {lead.loan_type}</>
            )}
          </Grid>

          <Grid item xs={6}>
            {editing ? (
              <TextField
                name="amount"
                label="Amount"
                type="number"
                value={lead.amount || ""}
                onChange={handleChange}
                fullWidth
              />
            ) : (
              <><strong>Amount:</strong> ₹ {lead.amount}</>
            )}
          </Grid>

          <Grid item xs={6}>
            {editing ? (
              <TextField
                name="cibil"
                label="CIBIL"
                value={lead.cibil || ""}
                onChange={handleChange}
                fullWidth
              />
            ) : (
              <><strong>CIBIL:</strong> {lead.cibil || "—"}</>
            )}
          </Grid>

          <Grid item xs={6}>
            {editing ? (
              <TextField
                name="employment_type"
                label="Employment Type"
                value={lead.employment_type || ""}
                onChange={handleChange}
                fullWidth
              />
            ) : (
              <><strong>Employment Type:</strong> {lead.employment_type || "—"}</>
            )}
          </Grid>

          <Grid item xs={6}>
            {editing ? (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={lead.status || ""}
                  onChange={handleChange}
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="ineligible">Ineligible</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <><strong>Status:</strong> {lead.status || "—"}</>
            )}
          </Grid>

          <Grid item xs={12}>
            {editing ? (
              <TextField
                name="note"
                label="Note"
                multiline
                rows={3}
                value={lead.note || ""}
                onChange={handleChange}
                fullWidth
              />
            ) : (
              <><strong>Note:</strong><br />{lead.note || "—"}</>
            )}
          </Grid>

          <Grid item xs={6}>
            {editing ? (
              <TextField
                name="assigned_company"
                label="Assigned Company"
                value={lead.assigned_company || ""}
                onChange={handleChange}
                fullWidth
              />
            ) : (
              <><strong>Assigned Company:</strong> {lead.assigned_company || "—"}</>
            )}
          </Grid>

          <Grid item xs={6}>
            {editing ? (
              <TextField
                name="assigned_person"
                label="Assigned Person"
                value={lead.assigned_person || ""}
                onChange={handleChange}
                fullWidth
              />
            ) : (
              <><strong>Assigned Person:</strong> {lead.assigned_person || "—"}</>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" gutterBottom>Uploaded Documents</Typography>
        <Grid container spacing={1}>
          {[
            "aadhar_front_url", "aadhar_back_url", "pancard_url",
            "bank_statement_url", "payslips_url", "it_returns_url"
          ].map((field) =>
            lead[field] ? (
              <Grid item xs={12} sm={6} key={field}>
                <a href={lead[field]} target="_blank" rel="noopener noreferrer">
                  {field.replace(/_/g, " ").replace("url", "").toUpperCase()}
                </a>
              </Grid>
            ) : null
          )}
        </Grid>

        <Box mt={4} display="flex" gap={2}>
          {!editing ? (
            <Button variant="outlined" onClick={() => setEditing(true)}>Edit</Button>
          ) : (
            <>
              <Button variant="contained" onClick={handleUpdate}>Save</Button>
              <Button variant="outlined" onClick={() => setEditing(false)}>Cancel</Button>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ViewLead;